'use client'

import { CheckCircleOutline, ErrorOutline, HourglassEmpty, StopCircleOutlined } from '@mui/icons-material'
import { Box, CircularProgress, Typography } from '@mui/material'
import { type DynamicToolUIPart, type ToolUIPart, getToolName } from 'ai'
import { useTranslations } from 'next-intl'

import toolCallLabel from '../stream/toolCalls'

export type ToolPart = ToolUIPart | DynamicToolUIPart

type ToolCallPartProps = {
  part: ToolPart
  /** The turn ended, so a call that has not produced its output shows as stopped instead of running. */
  interrupted?: boolean
}

/**
 * One tool call: what it ran and whether it is running, done, stopped or failed. A failure shows only its state;
 * the error text stays out of the UI.
 */
export default function ToolCallPart({ part, interrupted = false }: ToolCallPartProps) {
  const t = useTranslations('features.AssistantV2')
  const failed = isToolFailed(part)

  return (
    <Box
      data-testid="assistant-tool-call"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: failed ? 'error.main' : 'text.secondary',
        minWidth: 0
      }}
    >
      <StateIcon part={part} interrupted={interrupted} t={t} />
      <Typography variant="caption" noWrap sx={{ fontFamily: 'monospace' }}>
        {toolCallLabel(getToolName(part), part.input)}
      </Typography>
    </Box>
  )
}

export function isToolFailed(part: ToolPart): boolean {
  return part.state === 'output-error' || part.state === 'output-denied'
}

export function isToolRunning(part: ToolPart): boolean {
  return part.state === 'input-streaming' || part.state === 'input-available'
}

type StateIconProps = {
  part: ToolPart
  interrupted: boolean
  t: ReturnType<typeof useTranslations<'features.AssistantV2'>>
}

function StateIcon({ part, interrupted, t }: StateIconProps) {
  if (interrupted && isToolRunning(part)) {
    return <StopCircleOutlined fontSize="small" titleAccess={t('toolStateStopped')} />
  }
  switch (part.state) {
    case 'output-available':
      return <CheckCircleOutline fontSize="small" color="success" titleAccess={t('toolStateDone')} />
    case 'output-error':
    case 'output-denied':
      return <ErrorOutline fontSize="small" color="error" titleAccess={t('toolStateFailed')} />
    case 'approval-requested':
    case 'approval-responded':
      return <HourglassEmpty fontSize="small" titleAccess={t('toolStateAwaitingApproval')} />
    default:
      return <CircularProgress size={14} aria-label={t('toolStateRunning')} />
  }
}
